/* ==========================================================================
   THE MORTGAGE NINJA - MOTION ENGINE
   Portable with ninja-tokens.css + ninja-motion.css.

   Layer A  ambient canvas field, always running behind navy sections
   Layer B  the hero load timeline
   Layer C  scroll-driven reveals, parallax, counters, path draws, pinning
   Layer D  micro-interactions: magnetic, tilt, header morph, page wipe

   Intensity is read from <html data-motion="full|reduced|off">, which the
   theme setting writes. "off" and prefers-reduced-motion take the same path:
   everything lands in its resting state immediately.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var SETTING = root.getAttribute('data-motion') || 'full';
  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mqCoarse = window.matchMedia('(pointer: coarse)');
  var mqNarrow = window.matchMedia('(max-width: 767px)');

  // One authority for "should anything move". Re-read live so a user toggling
  // the OS setting mid-session is respected without a reload.
  function calm() { return SETTING === 'off' || mqReduce.matches; }
  function lite() { return calm() || SETTING === 'reduced'; }

  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  var hasGsap = !!(gsap && ScrollTrigger);
  if (hasGsap) gsap.registerPlugin(ScrollTrigger);

  /* ======================================================================
     Utilities
     ====================================================================== */

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  // Cheap once-per-frame scheduler shared by the pointer handlers.
  var rafQueue = [], rafPending = false;
  function onFrame(fn) {
    rafQueue.push(fn);
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(function () {
      var q = rafQueue; rafQueue = []; rafPending = false;
      for (var i = 0; i < q.length; i++) q[i]();
    });
  }

  /* ======================================================================
     Smooth scroll (Lenis)
     Skipped entirely under reduced motion: hijacking scroll for a user who
     asked for less motion is exactly the wrong move.
     ====================================================================== */
  var lenis = null;
  function initSmoothScroll() {
    // window.Lenis only exists when the theme setting loaded the script, so
    // this is also the off switch.
    if (lite() || !window.Lenis) return;
    // lerp is the whole feel of the page. 0.09 (below Lenis's own 0.1 default)
    // reads as lag, not smoothness: the wheel stops and the page keeps coasting
    // for half a second. 0.16 still eases, but lands where you pointed it.
    lenis = new window.Lenis({ lerp: 0.16, wheelMultiplier: 1.15, smoothWheel: true });
    // Exposed so the instance can be inspected or stopped from the console.
    window.ninjaLenis = lenis;
    if (hasGsap) {
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
      gsap.ticker.lagSmoothing(0);
    } else {
      requestAnimationFrame(function loop(t) { lenis.raf(t); requestAnimationFrame(loop); });
    }
  }

  /* ======================================================================
     LAYER A - Ambient canvas
     Navy gradient mesh on independent sine paths, gold particles drifting
     upward, a few rendered as rotating shuriken. Pauses when off-screen,
     throttles to 30fps on mobile.
     ====================================================================== */
  function initAmbient() {
    var cv = $('#ninja-ambient');
    if (!cv) return;
    if (calm()) { cv.style.display = 'none'; return; }

    var ctx = cv.getContext('2d', { alpha: true });
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var W = 0, H = 0, running = true, last = 0;
    var narrow = mqNarrow.matches;
    var fps = (narrow || mqCoarse.matches) ? 30 : 60;
    var frameGap = 1000 / fps;

    var BLOBS = [
      { hue: 'rgba(30, 58, 102,',  r: 0.55, ax: 0.30, ay: 0.22, sx: 0.00007, sy: 0.00009, ph: 0.0, a: 0.55 },
      { hue: 'rgba(21, 42, 78,',   r: 0.48, ax: 0.26, ay: 0.30, sx: 0.00005, sy: 0.00006, ph: 2.1, a: 0.60 },
      { hue: 'rgba(201, 164, 76,', r: 0.30, ax: 0.34, ay: 0.18, sx: 0.00004, sy: 0.00008, ph: 4.2, a: 0.10 }
    ];

    var COUNT = narrow ? 18 : 40;
    var parts = [];
    function seed() {
      parts.length = 0;
      for (var i = 0; i < COUNT; i++) {
        parts.push({
          x: Math.random(),
          y: Math.random(),
          size: 1 + Math.random() * 2.4,
          vy: 0.00006 + Math.random() * 0.00013,
          wob: Math.random() * Math.PI * 2,
          wobSpeed: 0.0004 + Math.random() * 0.0009,
          alpha: 0.15 + Math.random() * 0.25,
          // roughly one in five is a shuriken glyph rather than a dot
          star: Math.random() < 0.2,
          rot: Math.random() * Math.PI * 2,
          spin: (Math.random() - 0.5) * 0.0016
        });
      }
    }

    function resize() {
      W = cv.clientWidth; H = cv.clientHeight;
      cv.width = Math.round(W * dpr);
      cv.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      var wasNarrow = narrow;
      narrow = mqNarrow.matches;
      fps = (narrow || mqCoarse.matches) ? 30 : 60;
      frameGap = 1000 / fps;
      if (wasNarrow !== narrow) { COUNT = narrow ? 18 : 40; seed(); }
    }

    function shuriken(x, y, s, rot, alpha) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rot);
      ctx.fillStyle = 'rgba(240, 212, 134,' + alpha + ')';
      ctx.beginPath();
      // four-point star: alternating long and short radii
      for (var i = 0; i < 8; i++) {
        var a = (Math.PI / 4) * i;
        var r = (i % 2 === 0) ? s * 2.6 : s * 0.75;
        var px = Math.cos(a) * r, py = Math.sin(a) * r;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    function draw(t) {
      if (!running) return;
      requestAnimationFrame(draw);
      if (t - last < frameGap) return;
      last = t;

      ctx.clearRect(0, 0, W, H);

      // gradient mesh
      for (var b = 0; b < BLOBS.length; b++) {
        var o = BLOBS[b];
        var cx = W * (0.5 + Math.sin(t * o.sx + o.ph) * o.ax);
        var cy = H * (0.5 + Math.cos(t * o.sy + o.ph) * o.ay);
        var rad = Math.max(W, H) * o.r;
        var g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rad);
        g.addColorStop(0, o.hue + o.a + ')');
        g.addColorStop(1, o.hue + '0)');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
      }

      // particles
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i];
        p.y -= p.vy * (t - (p._t || t));
        p._t = t;
        p.wob += p.wobSpeed;
        if (p.y < -0.05) { p.y = 1.05; p.x = Math.random(); }
        var x = (p.x + Math.sin(p.wob) * 0.03) * W;
        var y = p.y * H;
        if (p.star) {
          p.rot += p.spin * 16;
          shuriken(x, y, p.size, p.rot, p.alpha);
        } else {
          ctx.fillStyle = 'rgba(201, 164, 76,' + p.alpha + ')';
          ctx.beginPath();
          ctx.arc(x, y, p.size, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    resize();
    seed();
    window.addEventListener('resize', resize, { passive: true });

    // Stop the loop the moment the canvas leaves the viewport, and when the
    // tab is hidden. An ambient field nobody can see is pure battery drain.
    var io = new IntersectionObserver(function (entries) {
      var visible = entries[0].isIntersecting;
      if (visible && !running) { running = true; last = 0; requestAnimationFrame(draw); }
      else if (!visible) { running = false; }
    }, { threshold: 0 });
    io.observe(cv);

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { running = false; }
      else if (!running) { running = true; last = 0; requestAnimationFrame(draw); }
    });

    requestAnimationFrame(draw);
  }

  /* ======================================================================
     LAYER C - Reveals, stagger, word splits
     IntersectionObserver, not ScrollTrigger, for the plain reveals. There are
     hundreds of these across the landing pages and IO is far cheaper.
     ====================================================================== */

  function splitWords() {
    $$('[data-words]').forEach(function (el) {
      if (el.dataset.split === '1') return;
      // Only split headings whose content is plain text.
      if (el.children.length) { el.dataset.split = '1'; return; }
      var words = el.textContent.trim().split(/\s+/);
      el.textContent = '';
      el.classList.add('words');
      // "auto" plays on its own the moment the spans exist. Anything else
      // waits for the scroll reveal to add .is-in.
      if (el.dataset.words === 'auto') el.classList.add('words--auto');
      words.forEach(function (word, i) {
        var outer = document.createElement('span');
        outer.className = 'w';
        outer.style.setProperty('--w-i', i);
        var inner = document.createElement('span');
        inner.textContent = word;
        outer.appendChild(inner);
        el.appendChild(outer);
        if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      });
      el.dataset.split = '1';
    });
  }

  function initReveals() {
    // Auto-stagger siblings that did not get an explicit delay.
    $$('[data-stagger]').forEach(function (group) {
      var step = parseInt(group.dataset.stagger, 10) || 80;
      var kids = $$('.rv, .blade', group).filter(function (k) {
        return k.parentElement === group || k.closest('[data-stagger]') === group;
      });
      kids.forEach(function (k, i) {
        if (k.style.getPropertyValue('--rv-delay')) return;
        k.style.setProperty('--rv-delay', Math.min(i * step, 540) + 'ms');
      });
    });

    // Prime SVG path lengths so the dash offsets are exact, not guessed.
    $$('.draw').forEach(function (path) {
      if (typeof path.getTotalLength !== 'function') return;
      var len = Math.ceil(path.getTotalLength());
      path.style.setProperty('--len', len);
    });

    var targets = $$('.rv, .blade, .rule, .draw:not(.draw--scrub), .words:not(.words--auto)');
    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (t) { t.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);   // reveal is a one-shot, stop watching
      });
      // threshold is a ratio of the element, so anything taller than the
      // viewport can never satisfy a fixed 0.16 and would stay hidden forever
      // (a long .prose column on a phone). Trigger on first pixel instead and
      // let the negative bottom margin supply the "meaningfully in view" delay,
      // which behaves the same for short blocks and is height-independent.
    }, { threshold: 0, rootMargin: '0px 0px -12% 0px' });
    targets.forEach(function (t) { io.observe(t); });
  }

  /* ======================================================================
     LAYER C - Counters
     ====================================================================== */
  function animateCount(el, to, dur, decimals, prefix, suffix) {
    var start = performance.now();
    var from = parseFloat(el.dataset.from || 0);
    function step(now) {
      var p = clamp((now - start) / dur, 0, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      var v = from + (to - from) * eased;
      el.textContent = prefix + v.toLocaleString('en-CA', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }) + suffix;
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function initCounters() {
    var els = $$('[data-count]');
    if (!els.length) return;
    if (calm()) {
      els.forEach(function (el) {
        var to = parseFloat(el.dataset.count);
        var d = parseInt(el.dataset.decimals || 0, 10);
        el.textContent = (el.dataset.prefix || '') + to.toLocaleString('en-CA', {
          minimumFractionDigits: d, maximumFractionDigits: d
        }) + (el.dataset.suffix || '');
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        animateCount(
          el,
          parseFloat(el.dataset.count),
          parseInt(el.dataset.dur || 1400, 10),
          parseInt(el.dataset.decimals || 0, 10),
          el.dataset.prefix || '',
          el.dataset.suffix || ''
        );
        io.unobserve(el);
      });
    }, { threshold: 0.5 });
    els.forEach(function (el) { io.observe(el); });
  }

  // Exposed so the calculators can re-run a count on every input change.
  window.ninjaCountTo = function (el, to, decimals, prefix, suffix) {
    if (!el) return;
    var d = decimals || 0;
    var fmt = function (v) {
      return (prefix || '') + v.toLocaleString('en-CA', {
        minimumFractionDigits: d, maximumFractionDigits: d
      }) + (suffix || '');
    };
    if (calm()) { el.textContent = fmt(to); el.dataset.from = to; return; }
    el.dataset.from = parseFloat(el.dataset.from || 0);
    animateCount(el, to, 620, d, prefix || '', suffix || '');
    el.dataset.from = to;
  };

  /* ======================================================================
     LAYER C - Parallax and scrubbed path draws (GSAP)
     ====================================================================== */
  function initScrollScenes() {
    // Anything whose visible state is driven by a scrub has to resolve to its
    // finished state when the scrub does not run, or that content is simply
    // never readable: greyed-out steps, undrawn routes, columns stuck at 0.25
    // opacity. Reduced motion means less movement, not less content.
    if (!hasGsap || lite()) {
      $$('[data-approval-node], [data-approval-dot]').forEach(function (n) {
        n.classList.add('is-lit');
      });
      $$('.approval__line, [data-route]').forEach(function (el) {
        el.style.setProperty('--p', 1);
      });
      return;
    }

    // Depth parallax on decorative layers, 0.2 to 0.5 speed.
    $$('[data-parallax]').forEach(function (el) {
      var speed = parseFloat(el.dataset.parallax) || 0.3;
      gsap.to(el, {
        yPercent: speed * 100 * -1,
        ease: 'none',
        scrollTrigger: {
          trigger: el.closest('[data-parallax-scope]') || el.parentElement,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
          invalidateOnRefresh: true
        }
      });
    });

    // Broker vs bank: the two columns slide in from opposite sides, scrubbed.
    var vs = $('[data-vs]');
    if (vs) {
      var tl = gsap.timeline({
        scrollTrigger: {
          trigger: vs,
          start: 'top 78%',
          end: 'top 28%',
          scrub: 0.6,
          invalidateOnRefresh: true
        }
      });
      // Below 860px the two columns stack full width, so a 14% horizontal
      // slide pushes each card past the viewport edge and gives the phone
      // sideways scroll. The opposed slide only makes sense side by side.
      var vsX = window.matchMedia('(min-width: 860px)').matches ? 14 : 0;
      // No opacity in this tween. A scrubbed fade strands the copy half
      // transparent whenever the trigger positions are stale, which reads as
      // a blurred section rather than an animation.
      tl.from(vs.querySelector('[data-vs-bank]'), { xPercent: -vsX, ease: 'none' }, 0)
        .from(vs.querySelector('[data-vs-broker]'), { xPercent: vsX, ease: 'none' }, 0)
        .from(vs.querySelector('[data-vs-divider]'), { scaleY: 0, ease: 'none' }, 0);
    }

    // The Approval Path: pinned, the route draws step to step as you scroll,
    // each node popping as the line reaches it. This is a real sequence, so a
    // scrubbed pin is the honest way to show it.
    var path = $('[data-approval]');
    if (path) {
      var line = $('.approval__line', path);
      var steps = $$('[data-approval-node]', path);   // the three cards
      var dots  = $$('[data-approval-dot]', path);    // the three nodes on the route
      var n = steps.length || dots.length;

      ScrollTrigger.create({
        trigger: path,
        start: 'top top',
        end: '+=' + (n * 420),
        pin: $('[data-approval-pin]', path) || path,
        scrub: 0.5,
        invalidateOnRefresh: true,
        onUpdate: function (self) {
          var p = self.progress;
          if (line) line.style.setProperty('--p', p);
          for (var i = 0; i < n; i++) {
            // light each step slightly before the line physically reaches it
            var lit = p >= ((i + 0.55) / n) * 0.92;
            if (steps[i]) steps[i].classList.toggle('is-lit', lit);
            if (dots[i])  dots[i].classList.toggle('is-lit', lit);
          }
        }
      });

      // Step one reads as active before the pin engages, so the section never
      // opens fully greyed out.
      if (steps[0]) steps[0].classList.add('is-lit');
      if (dots[0])  dots[0].classList.add('is-lit');
    }

    // Service-area routes draw as the map enters.
    $$('[data-route]').forEach(function (route) {
      gsap.to(route, {
        '--p': 1,
        ease: 'none',
        scrollTrigger: { trigger: route.closest('section'), start: 'top 72%', end: 'bottom 60%', scrub: 0.4 }
      });
    });
  }

  /* ======================================================================
     LAYER B - Hero timeline
     The H1 is never withheld: it is in the DOM painted and only its decorative
     layers animate, so the hero timeline can never delay LCP.
     ====================================================================== */
  function initHero() {
    var hero = $('[data-hero]');
    if (!hero) return;

    var crest   = $('[data-hero-crest]', hero);
    var houses  = $$('[data-hero-house] path, [data-hero-house] rect, [data-hero-house] polyline', hero);
    var key     = $('[data-hero-key]', hero);
    var heading = $('[data-hero-h1]', hero);
    var sub     = $('[data-hero-sub]', hero);
    var ctas    = $('[data-hero-ctas]', hero);
    var orbits  = $$('[data-orbit]', hero);
    var skyline = $('[data-skyline]', hero);
    var eyebrow = $('[data-hero-eyebrow]', hero);
    var aura    = $('[data-hero-aura]', hero);
    var ring    = $('[data-hero-ring]', hero);
    var ringDash = ring && ring.querySelector('.hero__ring-dash');
    var ringArc  = ring && ring.querySelector('.hero__ring-arc');
    var copy    = $('.hero__copy', hero);
    var stage   = $('.hero__stage', hero);
    var cue     = $('[data-hero-scroll]', hero);

    if (calm()) {
      hero.classList.add('is-ready');
      if (heading) heading.classList.add('is-in');
      return;
    }

    // Prime the house outline path lengths before the draw.
    houses.forEach(function (p) {
      if (typeof p.getTotalLength !== 'function') return;
      var len = Math.ceil(p.getTotalLength());
      p.style.strokeDasharray = len;
      p.style.strokeDashoffset = len;
    });

    hero.classList.add('is-ready');

    if (!hasGsap) {
      if (heading) heading.classList.add('is-in');
      houses.forEach(function (p) { p.style.strokeDashoffset = 0; });
      return;
    }

    var tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    // 1. eyebrow wipes in from the left
    if (eyebrow) tl.from(eyebrow, { x: -18, opacity: 0, duration: 0.55 }, 0.05);

    // 2. the aura blooms, the ring snaps round, then the crest lands.
    //    The crest overshoots and settles rather than easing flat, which is
    //    the difference between "it faded in" and "it arrived".
    if (aura) {
      tl.fromTo(aura, { scale: 0.4, opacity: 0 },
        { scale: 1, opacity: 1, duration: 1.1, ease: 'power2.out' }, 0);
      gsap.to(aura, { scale: 1.09, opacity: 0.72, duration: 3.6, repeat: -1, yoyo: true, ease: 'sine.inOut', delay: 1.2 });
    }
    if (ringDash) {
      tl.fromTo(ringDash, { scale: 0.55, opacity: 0, rotate: -120 },
        { scale: 1, opacity: 1, rotate: 0, duration: 1.15, ease: 'power4.out' }, 0.1);
      gsap.to(ringDash, { rotate: 360, duration: 46, repeat: -1, ease: 'none', transformOrigin: '50% 50%' });
    }
    if (ringArc) {
      tl.fromTo(ringArc, { strokeDashoffset: 553, opacity: 0 },
        { strokeDashoffset: 0, opacity: 1, duration: 1.5, ease: 'power2.inOut' }, 0.2);
      gsap.to(ringArc, { rotate: -360, duration: 24, repeat: -1, ease: 'none', transformOrigin: '50% 50%' });
    }
    if (crest) {
      tl.fromTo(crest,
        { scale: 0.62, opacity: 0, rotate: -14, filter: 'blur(14px)' },
        { scale: 1, opacity: 1, rotate: 0, filter: 'blur(0px)', duration: 1.25, ease: 'back.out(1.5)' }, 0.15)
        .add(function () { crest.classList.add('shimmer--hover'); }, 0.7);
      // A slow breath keeps the stage alive once the entrance is over.
      gsap.to(crest, { scale: 1.028, duration: 5.5, repeat: -1, yoyo: true, ease: 'sine.inOut', delay: 1.6 });
    }

    // 3. the key rotates and unlocks, then the house outline draws itself
    if (key) tl.fromTo(key, { rotate: 0 }, { rotate: 90, duration: 0.6, transformOrigin: '50% 70%' }, 0.5);
    if (houses.length) {
      tl.fromTo('[data-hero-house]', { scale: 0.4, opacity: 0 },
          { scale: 1, opacity: 1, duration: 0.7, ease: 'back.out(2.2)' }, 0.6)
        .to(houses, { strokeDashoffset: 0, duration: 1.2, stagger: 0.08, ease: 'power2.inOut' }, 0.75)
        .to('[data-hero-house]', { '--fill-op': 1, duration: 0.5 }, 1.7);
    }

    // 4. H1 words reveal on a staggered mask-up, 60ms apart
    if (heading) tl.add(function () { heading.classList.add('is-in'); }, 0.65);

    // 5. subhead and CTAs, each rising with a slight blur burn-off
    if (sub)  tl.from(sub,  { y: 28, opacity: 0, filter: 'blur(8px)', duration: 0.8 }, 1.15);
    if (ctas) {
      tl.from(ctas.querySelectorAll('.btn'), {
        y: 30, opacity: 0, scale: 0.94, duration: 0.7, stagger: 0.09, ease: 'back.out(1.6)'
      }, 1.25);
    }
    if (cue) tl.from(cue, { y: -14, opacity: 0, duration: 0.6 }, 1.8);

    // 6. shuriken begin orbiting on independent elliptical paths.
    //    Alternating direction reads as depth rather than as one spinning ring.
    orbits.forEach(function (o, i) {
      var rx = 118 + i * 40, ry = 74 + i * 27;
      var dur = 11 + i * 4.5;
      var dir = i % 2 ? -1 : 1;
      gsap.set(o, { opacity: 0 });
      tl.to(o, { opacity: 1, duration: 0.6 }, 1.05 + i * 0.12);
      gsap.to(o, {
        duration: dur, repeat: -1, ease: 'none',
        onUpdate: function () {
          var a = dir * this.progress() * Math.PI * 2 + i * 2.1;
          gsap.set(o, {
            x: Math.cos(a) * rx,
            y: Math.sin(a) * ry,
            rotate: a * 140,
            scale: 0.82 + (Math.sin(a) + 1) * 0.18
          });
        }
      });
    });

    // 7. Scroll drive. The copy leaves faster than the stage, the stage turns
    //    and shrinks, and the aura fades: the hero pulls apart as you scroll
    //    instead of sliding away as one flat plate.
    if (!lite()) {
      var st = { trigger: hero, start: 'top top', end: 'bottom top', scrub: 0.3, invalidateOnRefresh: true };
      if (copy)  gsap.to(copy,  { yPercent: -22, opacity: 0.15, ease: 'none', scrollTrigger: st });
      if (stage) gsap.to(stage, { yPercent: 12, scale: 0.82, rotate: 9, ease: 'none', scrollTrigger: st });
      if (aura)  gsap.to(aura,  { opacity: 0, ease: 'none', scrollTrigger: st });
      if (ringArc) gsap.to(ringArc, { rotate: '+=200', ease: 'none', scrollTrigger: st, transformOrigin: '50% 50%' });
      if (cue)   gsap.to(cue,   { opacity: 0, y: 24, ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: '+=280', scrub: true } });
    }

    // 7. skyline parallax at 0.3, with windows lighting on random intervals
    if (skyline) {
      gsap.to(skyline, {
        yPercent: -34, ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true, invalidateOnRefresh: true }
      });
      // Only a sample of the windows flicker. The skyline has ~160 of them and
      // running a tween on every one buys no visible difference for the cost.
      var wins = $$('[data-window]', skyline);
      var pick = [];
      for (var wi = 0; wi < wins.length; wi += Math.max(1, Math.round(wins.length / 24))) {
        pick.push(wins[wi]);
      }
      pick.forEach(function (w) {
        var flicker = function () {
          gsap.to(w, {
            opacity: 0.18 + Math.random() * 0.7,
            duration: 0.9 + Math.random() * 1.6,
            delay: Math.random() * 5,
            onComplete: flicker
          });
        };
        flicker();
      });
    }

    // Continuous: pointer parallax, layers at different depths.
    var layers = [
      { el: crest,   depth: 16 },
      // Same depth as the crest, or the ring slides off it on pointer move.
      { el: ring,    depth: 16 },
      { el: aura,    depth: 30 },
      { el: skyline, depth: 8 }
    ].filter(function (l) { return l.el; });
    orbits.forEach(function (o) { layers.push({ el: o.parentElement, depth: 24 }); });

    // GSAP writes an inline `transform` on any element it tweens, which beats
    // the CSS `translate3d(var(--px), var(--py), 0)`. So when GSAP is driving,
    // hand it the parallax offset too and let it compose the matrix.
    function place(el, x, y) {
      if (hasGsap) gsap.set(el, { x: x, y: y });
      else {
        el.style.setProperty('--px', x.toFixed(2) + 'px');
        el.style.setProperty('--py', y.toFixed(2) + 'px');
      }
    }

    if (!mqCoarse.matches && layers.length) {
      var tx = 0, ty = 0, cx = 0, cy = 0, ticking = false;
      hero.addEventListener('pointermove', function (e) {
        var r = hero.getBoundingClientRect();
        tx = ((e.clientX - r.left) / r.width - 0.5) * -2;
        ty = ((e.clientY - r.top) / r.height - 0.5) * -2;
        if (ticking) return;
        ticking = true;
        onFrame(function loop() {
          cx += (tx - cx) * 0.08; cy += (ty - cy) * 0.08;
          layers.forEach(function (l) {
            place(l.el, cx * l.depth, cy * l.depth);
          });
          if (Math.abs(tx - cx) > 0.001 || Math.abs(ty - cy) > 0.001) onFrame(loop);
          else ticking = false;
        });
      }, { passive: true });
    } else if (mqCoarse.matches) {
      // Touch devices: a gentle auto-drift stands in for cursor parallax.
      // deviceorientation needs a user gesture to request permission on iOS,
      // and a hero is the wrong place to prompt for a sensor, so drift it is.
      layers.forEach(function (l, i) {
        gsap.to(l.el, {
          duration: 9 + i * 3, repeat: -1, yoyo: true, ease: 'sine.inOut',
          onUpdate: function () {
            var p = this.progress();
            place(l.el, (p - 0.5) * l.depth * 0.7, (0.5 - p) * l.depth * 0.5);
          }
        });
      });
    }
  }

  /* ======================================================================
     LAYER D - Micro-interactions
     ====================================================================== */

  function initMagnetic() {
    if (lite() || mqCoarse.matches) return;
    $$('.magnetic').forEach(function (el) {
      var R = 60;
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var dx = e.clientX - (r.left + r.width / 2);
        var dy = e.clientY - (r.top + r.height / 2);
        if (Math.hypot(dx, dy) > R + Math.max(r.width, r.height) / 2) return;
        el.classList.add('is-pulled');
        el.style.setProperty('--mx', clamp(dx * 0.32, -R / 2, R / 2).toFixed(1) + 'px');
        el.style.setProperty('--my', clamp(dy * 0.32, -R / 2, R / 2).toFixed(1) + 'px');
      }, { passive: true });
      el.addEventListener('pointerleave', function () {
        el.classList.remove('is-pulled');
        el.style.setProperty('--mx', '0px');
        el.style.setProperty('--my', '0px');
      }, { passive: true });
    });
  }

  function initTilt() {
    if (lite() || mqCoarse.matches) return;
    $$('.tilt').forEach(function (el) {
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        el.classList.add('is-hot');
        el.style.setProperty('--ry', (px * 4).toFixed(2) + 'deg');
        el.style.setProperty('--rx', (-py * 4).toFixed(2) + 'deg');
        // Same read, second use: the gold spotlight tracks the pointer.
        el.style.setProperty('--sx', (px * 100 + 50).toFixed(1) + '%');
        el.style.setProperty('--sy', (py * 100 + 50).toFixed(1) + '%');
      }, { passive: true });
      el.addEventListener('pointerleave', function () {
        el.classList.remove('is-hot');
        el.style.setProperty('--rx', '0deg');
        el.style.setProperty('--ry', '0deg');
      }, { passive: true });
    });
  }

  // Header morphs from transparent to frosted navy at 80px.
  function initHeader() {
    var header = $('[data-header]');
    if (!header) return;
    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;top:80px;left:0;width:1px;height:1px;pointer-events:none;';
    document.body.prepend(sentinel);
    new IntersectionObserver(function (entries) {
      header.classList.toggle('is-stuck', !entries[0].isIntersecting);
    }, { threshold: 0 }).observe(sentinel);
  }

  // Page transition: a navy wipe with a shuriken spin at the midpoint.
  function initPageWipe() {
    var wipe = $('[data-wipe]');
    if (!wipe || lite()) return;

    // Play the exit half on same-origin navigations.
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      if (a.target === '_blank' || a.hasAttribute('download')) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      var url;
      try { url = new URL(a.href, location.href); } catch (err) { return; }
      if (url.origin !== location.origin) return;
      if (url.pathname === location.pathname && url.hash) return;   // in-page anchor
      e.preventDefault();
      wipe.classList.add('is-closing');
      setTimeout(function () { location.href = a.href; }, 460);
    });

    // The arrival sweep is pure CSS and needs nothing here. On a bfcache
    // restore the overlay may still be mid-exit, so clear that state.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) wipe.classList.remove('is-closing');
    });
  }

  /* ======================================================================
     Boot
     ====================================================================== */
  function boot() {
    splitWords();
    initHeader();
    initReveals();
    initCounters();
    initAmbient();
    initSmoothScroll();
    initHero();
    initScrollScenes();
    initMagnetic();
    initTilt();
    initPageWipe();

    // ScrollTriggers measure against text. Fonts land after first paint and
    // change every height on the page, so refresh once they are in.
    if (hasGsap && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { ScrollTrigger.refresh(); });
    }
    // Images do the same, and the crest is heavy enough to land well after
    // fonts. Without this every trigger below it measures against a shorter
    // page than the one the visitor scrolls.
    if (hasGsap) {
      window.addEventListener('load', function () { ScrollTrigger.refresh(); });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
